import {useCallback, useEffect, useMemo, useState} from "react";
import {doc, updateDoc} from "firebase/firestore";
import {db} from "../../Firebase";
import {CLIENT_URL} from "../../context/FetchContext";
import {kLibrettoUserCollectionName} from "../../utils/CollectionConstants";
import {flushSessionEvents, trackSessionEvent} from "../../analytics/sessionEvents";
import {LibrettoPlan} from "../../utils/utils";
import {
    getInitialOnboardingDemoPlaybackId,
    getInitialOnboardingDesktopActivationAction,
    initialOnboardingVersion,
} from "./initialOnboardingConfig";

export const useInitialOnboardingFlow = ({
    userId,
    currentWorkspaceId,
    authContext,
    userBillingState,
    setUserBillingState,
    isMobile,
    isLifetimePlanUser,
}) => {
    const [initialOnboardingStep, setInitialOnboardingStep] = useState(null);
    const [initialOnboardingRole, setInitialOnboardingRole] = useState("");
    const [initialOnboardingFirstThingToSee, setInitialOnboardingFirstThingToSee] = useState("");
    const [trialModalOpen, setTrialModalOpen] = useState(false);
    const [isReopenedActivationModal, setIsReopenedActivationModal] = useState(false);
    const isFreePlanUser = Number(authContext.authState?.userPlan ?? authContext.getUserPlan?.()) === LibrettoPlan.StudioFree;

    const effectiveFirstThingToSee = String(
        initialOnboardingFirstThingToSee || userBillingState.onboardingFirstThingToSee || ""
    ).trim();

    const initialOnboardingDemoPlaybackId = useMemo(
        () => getInitialOnboardingDemoPlaybackId(effectiveFirstThingToSee),
        [effectiveFirstThingToSee]
    );
    const activationModalAutoAction = useMemo(() => {
        if (!trialModalOpen || isReopenedActivationModal || isMobile) return null;
        return getInitialOnboardingDesktopActivationAction(effectiveFirstThingToSee);
    }, [effectiveFirstThingToSee, isMobile, isReopenedActivationModal, trialModalOpen]);

    const displayedInitialOnboardingStep = useMemo(() => {
        if (initialOnboardingStep) return initialOnboardingStep;
        if (isMobile && userBillingState.loaded && userBillingState.hasCompletedInitialOnboarding) {
            return "mobile_handoff";
        }
        return null;
    }, [
        initialOnboardingStep,
        isMobile,
        userBillingState.loaded,
        userBillingState.hasCompletedInitialOnboarding,
    ]);

    useEffect(() => {
        setInitialOnboardingStep(null);
        setInitialOnboardingRole("");
        setInitialOnboardingFirstThingToSee("");
        setTrialModalOpen(false);
        setIsReopenedActivationModal(false);
    }, [userId, currentWorkspaceId]);

    const persistInitialOnboardingFields = useCallback(async (fields) => {
        if (!userId) return;
        try {
            const userRef = doc(db, kLibrettoUserCollectionName, userId);
            await updateDoc(userRef, fields);
        } catch (error) {
            console.error("[AgenticHome] Error updating initial onboarding fields:", error);
        }
    }, [userId]);

    useEffect(() => {
        if (!userId || !userBillingState.loaded) return;
        const safeRole = String(userBillingState.onboardingRole || "").trim();
        const safeFirstThingToSee = String(userBillingState.onboardingFirstThingToSee || "").trim();

        if (!initialOnboardingRole && safeRole) {
            setInitialOnboardingRole(safeRole);
        }
        if (!initialOnboardingFirstThingToSee && safeFirstThingToSee) {
            setInitialOnboardingFirstThingToSee(safeFirstThingToSee);
        }

        if (initialOnboardingStep) return;
        if (userBillingState.hasCompletedInitialOnboarding) return;

        setTrialModalOpen(false);
        if (!safeRole) {
            setInitialOnboardingStep("role");
            return;
        }
        if (!safeFirstThingToSee) {
            setInitialOnboardingStep("first_thing");
            return;
        }
        if (isMobile) {
            setInitialOnboardingStep("mobile_handoff");
        }
    }, [
        userId,
        userBillingState.loaded,
        userBillingState.hasCompletedInitialOnboarding,
        userBillingState.onboardingRole,
        userBillingState.onboardingFirstThingToSee,
        initialOnboardingRole,
        initialOnboardingFirstThingToSee,
        initialOnboardingStep,
        isMobile,
    ]);

    useEffect(() => {
        if (!userId || !userBillingState.loaded) return;
        if (initialOnboardingStep) {
            setTrialModalOpen(false);
            return;
        }
        if (!userBillingState.hasCompletedInitialOnboarding) {
            setTrialModalOpen(false);
            return;
        }
        if (isMobile) {
            setTrialModalOpen(false);
            return;
        }
        if (isLifetimePlanUser || isFreePlanUser) {
            setTrialModalOpen(false);
            return;
        }
        if (userBillingState.hasSeenTrialModal) {
            setTrialModalOpen(false);
            return;
        }
        setIsReopenedActivationModal(false);
        setTrialModalOpen(true);
    }, [
        userId,
        userBillingState.loaded,
        userBillingState.hasSeenTrialModal,
        userBillingState.hasCompletedInitialOnboarding,
        isLifetimePlanUser,
        isFreePlanUser,
        isMobile,
        initialOnboardingStep,
    ]);

    useEffect(() => {
        if (!userId || !userBillingState.loaded) return;
        if (userBillingState.hasCompletedInitialOnboarding) return;

        const safeRole = String(userBillingState.onboardingRole || "").trim();
        const safeFirstThingToSee = String(userBillingState.onboardingFirstThingToSee || "").trim();
        if (!safeRole || !safeFirstThingToSee) return;

        const repairedFields = {
            hasCompletedInitialOnboarding: true,
            initialOnboardingVersion,
            initialOnboardingCompletedAt: Date.now(),
        };

        setUserBillingState((prev) => ({
            ...prev,
            ...repairedFields,
        }));

        void persistInitialOnboardingFields(repairedFields);
    }, [
        userId,
        userBillingState.loaded,
        userBillingState.hasCompletedInitialOnboarding,
        userBillingState.onboardingRole,
        userBillingState.onboardingFirstThingToSee,
        persistInitialOnboardingFields,
        setUserBillingState,
    ]);

    const handleInitialOnboardingRoleSelect = useCallback((value) => {
        const safeValue = String(value || "").trim();
        if (!safeValue) return;

        trackSessionEvent(`initial_onboarding_a_role_selected_${safeValue}`);
        setInitialOnboardingRole(safeValue);
        setInitialOnboardingStep("first_thing");
        setUserBillingState((prev) => ({
            ...prev,
            onboardingRole: safeValue,
        }));

        void persistInitialOnboardingFields({
            onboardingRole: safeValue,
            initialOnboardingVersion,
        });
    }, [persistInitialOnboardingFields, setUserBillingState]);

    const handleInitialOnboardingFirstThingSelect = useCallback((value) => {
        const safeValue = String(value || "").trim();
        if (!safeValue) return;
        const safeRole = String(initialOnboardingRole || userBillingState.onboardingRole || "").trim();

        trackSessionEvent(`initial_onboarding_b_first_thing_selected_${safeValue}`);
        const nextFields = {
            ...(safeRole ? {onboardingRole: safeRole} : {}),
            onboardingFirstThingToSee: safeValue,
            hasCompletedInitialOnboarding: true,
            hasSeenTrialModal: false,
            initialOnboardingVersion,
            initialOnboardingCompletedAt: Date.now(),
        };

        setInitialOnboardingFirstThingToSee(safeValue);
        setTrialModalOpen(false);
        setUserBillingState((prev) => ({
            ...prev,
            ...nextFields,
        }));

        if (isMobile) {
            setInitialOnboardingStep("mobile_handoff");
        } else {
            setInitialOnboardingStep(null);
        }

        void persistInitialOnboardingFields(nextFields);
    }, [
        initialOnboardingRole,
        isMobile,
        persistInitialOnboardingFields,
        setUserBillingState,
        userBillingState.onboardingRole,
    ]);

    const handleContinueInitialOnboardingMobileDemo = useCallback(() => {
        setInitialOnboardingStep("mobile_handoff");
    }, []);

    const handleEmailMeLibrettoLink = useCallback(() => {
        const recipient = String(authContext.getUserEmail?.() || "").trim();
        const safeChoice = String(
            initialOnboardingFirstThingToSee || userBillingState.onboardingFirstThingToSee || ""
        ).trim();
        trackSessionEvent("initial_onboarding_mobile_email_link_clicked");
        if (safeChoice) {
            trackSessionEvent(`initial_onboarding_mobile_email_link_clicked_${safeChoice}`);
        }
        void flushSessionEvents();
        const dashboardUrl = `${CLIENT_URL}/dashboard`;
        const subject = encodeURIComponent("Open Libretto on your computer");
        const body = encodeURIComponent(`Open Libretto on your computer:\n\n${dashboardUrl}`);
        const mailtoUrl = `mailto:${recipient ? encodeURIComponent(recipient) : ""}?subject=${subject}&body=${body}`;
        window.location.href = mailtoUrl;
        setInitialOnboardingStep(null);
    }, [authContext, initialOnboardingFirstThingToSee, userBillingState.onboardingFirstThingToSee]);

    const markTrialModalSeen = useCallback(async () => {
        if (!userId || userBillingState.hasSeenTrialModal) return;
        setUserBillingState((prev) => ({...prev, hasSeenTrialModal: true}));
        try {
            const userRef = doc(db, kLibrettoUserCollectionName, userId);
            await updateDoc(userRef, {hasSeenTrialModal: true});
        } catch (error) {
            console.error("Error marking trial modal as seen:", error);
        }
    }, [userId, userBillingState.hasSeenTrialModal, setUserBillingState]);

    const handleCloseTrialModal = useCallback(() => {
        void markTrialModalSeen();
        setTrialModalOpen(false);
    }, [markTrialModalSeen]);

    const handleOpenActivationModal = useCallback(() => {
        setIsReopenedActivationModal(true);
        setTrialModalOpen(true);
    }, []);

    const hideTrialModal = useCallback(() => {
        setTrialModalOpen(false);
    }, []);

    return {
        initialOnboardingStep: displayedInitialOnboardingStep,
        initialOnboardingRole,
        initialOnboardingFirstThingToSee,
        initialOnboardingDemoPlaybackId,
        activationModalAutoAction,
        trialModalOpen,
        isReopenedActivationModal,
        handleInitialOnboardingRoleSelect,
        handleInitialOnboardingFirstThingSelect,
        handleContinueInitialOnboardingMobileDemo,
        handleEmailMeLibrettoLink,
        markTrialModalSeen,
        handleCloseTrialModal,
        handleOpenActivationModal,
        hideTrialModal,
    };
};
